import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShippingOutlined as ShippedIcon,
  Done as DeliveredIcon,
  RadioButtonUnchecked as PendingIcon
} from '@mui/icons-material';

// Hepsiburada marka rengi
const HB_ORANGE = '#ff6000';
const HB_ORANGE_LIGHT = '#fff0e6';

interface HepsiburadaOrder {
  id: string;
  orderId: string;
  orderNumber: string;
  date: string;
  status: string;
  totalAmount: number;
  itemsTotal?: number;
  items: any[];
  shippingAddress?: any;
  billingAddress?: any;
  cargoInfo?: any;
  trackingNumber?: string;
  shippingCompany?: string;
  shipmentPackageStatus?: string;
  marketplace?: string;
  n11OrderId?: string; // mapMarketplaceOrder içinde tüm pazaryerleri için marketplaceOrderId'ye eşlenir
  n11Status?: string;  // marketplaceStatus
  customerName?: string;
  customerEmail?: string;
}

interface HepsiburadaOrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: HepsiburadaOrder | null;
  getStatusChip: (status: string) => React.ReactElement;
}

// Farklı kaynaklardan gelen adres objelerini (ham webhook `deliveryAddress` /
// reconcile `BuildAddressObject` çıktısı) tek bir şekle normalize eder.
const normalizeAddress = (raw: any) => {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
    }
    return '';
  };
  const name = pick('fullName', 'name', 'recipientName');
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  return {
    name: name || firstLast,
    address: pick('address', 'fullAddress', 'addressLine', 'address1'),
    city: pick('city', 'cityName'),
    // Hepsiburada: town = ilçe, district = mahalle
    district: pick('town', 'townName', 'county', 'district', 'districtName'),
    neighborhood: pick('neighborhood', 'district', 'districtName'),
    phone: pick('phone', 'gsm', 'phoneNumber', 'alternatePhoneNumber'),
    postalCode: pick('postalCode', 'zipCode'),
    email: pick('email'),
    tcId: pick('tcId', 'identityNo', 'turkishIdentityNumber'),
    taxNumber: pick('taxNumber', 'taxId', 'vkn'),
    taxOffice: pick('taxOffice', 'taxHouse'),
    invoiceType: pick('invoiceType', 'companyName') // kurumsal ipucu
  };
};

// HB kargo bilgisi çoğu zaman ham RawJson içinde gömülü gelir; anahtarları derinlemesine ara.
const deepFindString = (obj: any, keys: string[], depth = 0): string => {
  if (!obj || typeof obj !== 'object' || depth > 6) return '';
  for (const k of Object.keys(obj)) {
    if (keys.some(t => t.toLowerCase() === k.toLowerCase())) {
      const v = obj[k];
      if (v !== undefined && v !== null && typeof v !== 'object' && String(v).trim() !== '') {
        return String(v);
      }
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === 'object') {
      const found = deepFindString(Array.isArray(v) ? { ...v } : v, keys, depth + 1);
      if (found) return found;
    }
  }
  return '';
};

const formatDate = (value?: string | number) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('tr-TR', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const HepsiburadaOrderDetailsModal: React.FC<HepsiburadaOrderDetailsModalProps> = ({
  open,
  onClose,
  order,
  getStatusChip
}) => {
  if (!order) return null;

  const shipping = normalizeAddress(order.shippingAddress);
  const billing = normalizeAddress(order.billingAddress);

  const itemsTotal = order.itemsTotal || 0;
  const shippingFee = (order.totalAmount || 0) - itemsTotal;

  // Kargo bilgisi: önce maplenmiş alanlar, sonra ham JSON derin arama
  const cargoCompany =
    order.shippingCompany ||
    deepFindString(order.cargoInfo, ['cargoCompany', 'cargoProviderName', 'shipmentCompany']);
  const trackingNumber =
    order.trackingNumber ||
    deepFindString(order.cargoInfo, ['trackingInfoCode', 'cargoTrackingNumber', 'trackingNumber']);
  const cargoBarcode = deepFindString(order.cargoInfo, ['barcode', 'cargoSenderNumber']);
  const trackingLink = deepFindString(order.cargoInfo, ['trackingInfoUrl', 'cargoTrackingLink', 'trackingUrl']);
  const hasCargo = Boolean(cargoCompany || trackingNumber || cargoBarcode || trackingLink);

  // Durumdan türetilen paket geçmişi (HB webhook'unda ayrı timeline gelmiyor)
  const status = (order.status || '').toLowerCase();
  const isCancelled = status === 'cancelled' || status === 'cancel_requested';
  const reachedConfirmed = ['confirmed', 'shipped', 'delivered'].includes(status);
  const reachedShipped = ['shipped', 'delivered'].includes(status);
  const reachedDelivered = status === 'delivered';

  type Step = { label: string; date?: string; done: boolean; kind: 'created' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' };
  const timeline: Step[] = isCancelled
    ? [
        { label: 'Sipariş Oluşturuldu', date: order.date, done: true, kind: 'created' },
        { label: status === 'cancel_requested' ? 'İptal Talebi Alındı' : 'Sipariş İptal Edildi', done: true, kind: 'cancelled' }
      ]
    : [
        { label: 'Sipariş Oluşturuldu', date: order.date, done: true, kind: 'created' },
        { label: 'Sipariş Onaylandı / Hazırlanıyor', done: reachedConfirmed, kind: 'confirmed' },
        { label: 'Kargoya Verildi', done: reachedShipped, kind: 'shipped' },
        { label: 'Teslim Edildi', done: reachedDelivered, kind: 'delivered' }
      ];

  const stepColor = (s: Step) => {
    if (!s.done) return '#bdbdbd';
    switch (s.kind) {
      case 'created': return '#2196f3';
      case 'confirmed': return HB_ORANGE;
      case 'shipped': return '#4caf50';
      case 'delivered': return '#2e7d32';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const stepIcon = (s: Step) => {
    if (!s.done) return <PendingIcon sx={{ color: '#fff', fontSize: 18 }} />;
    switch (s.kind) {
      case 'shipped': return <ShippedIcon sx={{ color: '#fff', fontSize: 18 }} />;
      case 'delivered': return <DeliveredIcon sx={{ color: '#fff', fontSize: 18 }} />;
      case 'cancelled': return <CancelIcon sx={{ color: '#fff', fontSize: 18 }} />;
      default: return <CheckCircleIcon sx={{ color: '#fff', fontSize: 18 }} />;
    }
  };

  const renderAddressCard = (addr: ReturnType<typeof normalizeAddress>, title: string, icon: React.ReactNode, showInvoiceExtras = false) => {
    if (!addr || (!addr.name && !addr.address && !addr.city)) return null;
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {icon} {title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {addr.name && <Typography variant="body2" fontWeight="medium">{addr.name}</Typography>}
            {addr.address && <Typography variant="body2">{addr.address}</Typography>}
            {(addr.neighborhood || addr.district || addr.city) && (
              <Typography variant="body2">
                {addr.neighborhood && addr.neighborhood !== addr.district ? `${addr.neighborhood}, ` : ''}
                {[addr.district, addr.city].filter(Boolean).join(', ')}
              </Typography>
            )}
            {addr.postalCode && <Typography variant="body2">Posta Kodu: {addr.postalCode}</Typography>}
            {addr.phone && <Typography variant="body2">Telefon: {addr.phone}</Typography>}
            {showInvoiceExtras && addr.taxNumber && (
              <Typography variant="body2" color="text.secondary">Vergi No: {addr.taxNumber}</Typography>
            )}
            {showInvoiceExtras && addr.taxOffice && (
              <Typography variant="body2" color="text.secondary">Vergi Dairesi: {addr.taxOffice}</Typography>
            )}
            {showInvoiceExtras && addr.tcId && (
              <Typography variant="body2" color="text.secondary">TC: {addr.tcId}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: HB_ORANGE_LIGHT,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: `1px solid ${HB_ORANGE}`
          }}>
            <StoreIcon sx={{ color: HB_ORANGE, mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: HB_ORANGE, fontWeight: 'bold' }}>
              Hepsiburada
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6">
              Hepsiburada Sipariş Detayları - #{order.orderNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customerName || 'Müşteri bilinmiyor'}{order.customerEmail ? ` - ${order.customerEmail}` : ''}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Sipariş Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  Hepsiburada Sipariş Bilgileri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">HB Sipariş No:</Typography>
                    <Typography variant="body2" fontWeight="medium">{order.orderNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">HB Sipariş ID:</Typography>
                    <Typography variant="body2" fontFamily="monospace">{order.n11OrderId || order.orderId}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Tarih:</Typography>
                    <Typography variant="body2">{formatDate(order.date) || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Sistem Durumu:</Typography>
                    {getStatusChip(order.status)}
                  </Box>
                  {(order.n11Status || order.shipmentPackageStatus) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">HB Durumu:</Typography>
                      <Chip
                        label={order.n11Status || order.shipmentPackageStatus}
                        size="small"
                        sx={{ bgcolor: HB_ORANGE_LIGHT, color: HB_ORANGE, fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fiyat Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  Fiyat Bilgileri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Ürünler Toplamı:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{itemsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Kargo Ücreti:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{(shippingFee > 0 ? shippingFee : 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold">Toplam Tutar:</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: HB_ORANGE }}>
                      ₺{(order.totalAmount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Kargo Bilgileri */}
          {hasCargo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <LocalShippingIcon sx={{ mr: 1, fontSize: 16 }} />
                    Kargo Bilgileri
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Kargo Firması:</Typography>
                      <Typography variant="body2" fontWeight="medium">{cargoCompany || 'Belirtilmemiş'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Takip Numarası:</Typography>
                      <Typography variant="body2" fontFamily="monospace">{trackingNumber || 'Yok'}</Typography>
                    </Box>
                    {cargoBarcode && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Barkod / Kargo Kodu:</Typography>
                        <Typography variant="body2" fontFamily="monospace">{cargoBarcode}</Typography>
                      </Box>
                    )}
                    {trackingLink && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Takip Linki:</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(trackingLink, '_blank')}
                          sx={{ fontSize: '0.75rem', color: HB_ORANGE, borderColor: HB_ORANGE }}
                        >
                          Takip Et
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Paket Geçmişi (durumdan türetilmiştir) */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <TimelineIcon sx={{ mr: 1, fontSize: 16 }} />
                  Paket Geçmişi
                </Typography>
                <List sx={{ p: 0 }}>
                  {timeline.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '50%',
                          bgcolor: stepColor(step),
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {stepIcon(step)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium" sx={{ color: step.done ? 'inherit' : 'text.disabled' }}>
                            {step.label}
                          </Typography>
                        }
                        secondary={
                          step.date && step.done ? (
                            <Typography variant="caption" color="text.secondary">{formatDate(step.date)}</Typography>
                          ) : undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Teslimat Adresi */}
          {shipping && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(shipping, 'Teslimat Adresi', <LocationOnIcon sx={{ mr: 1, fontSize: 16 }} />)}
            </Grid>
          )}

          {/* Fatura Adresi */}
          {billing && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(billing, 'Fatura Adresi', <ReceiptIcon sx={{ mr: 1, fontSize: 16 }} />, true)}
            </Grid>
          )}

          {/* Sipariş Edilen Ürünler */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  Hepsiburada Sipariş Ürünleri ({order.items?.length || 0} ürün)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(order.items || []).map((item: any, index: number) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={item.image} alt={item.name} variant="rounded" sx={{ width: 60, height: 60 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            HB Stok Kodu: {item.stockCode || item.n11StockCode || item.barcode || 'Yok'}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            HB Satır ID: {item.lineItemId || item.lineId || 'Yok'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                          <Typography variant="body2" fontWeight="medium">Adet: {item.quantity}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                          <Typography variant="body2" fontWeight="medium">
                            ₺{Number(item.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: HB_ORANGE }}>
                            Toplam: ₺{(Number(item.price || 0) * Number(item.quantity || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HepsiburadaOrderDetailsModal;
